/**
 * The other end of a "confirm your email address" link.
 *
 * Confirming is a POST, because it changes something — but the person got here
 * by clicking a link, so the page has to fire it itself rather than asking
 * them to press a second button for no reason.
 *
 * The `attempted` ref is load-bearing, not defensive noise: a verification
 * token is spent on first use, and React runs effects twice in development.
 * Without the guard the second run would consume a token the first run had
 * already used, and every developer would see "this link is no longer valid"
 * for a link that had just worked.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { Button, Spinner } from '../../components/ui';

type Outcome =
  | { state: 'checking' }
  | { state: 'confirmed'; email: string | null }
  | { state: 'failed'; message: string };

export function VerifyEmailPage(): ReactNode {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome>({ state: 'checking' });
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    api
      .post<{ verified: boolean; email: string | null }>(`/auth/verify-email/${token}`)
      .then((result) => {
        setOutcome(
          result.verified
            ? { state: 'confirmed', email: result.email }
            : {
                state: 'failed',
                message: 'That confirmation link is no longer valid.',
              },
        );
      })
      .catch((caught: unknown) => {
        setOutcome({
          state: 'failed',
          message:
            caught instanceof ApiError
              ? caught.message
              : 'That confirmation link could not be used.',
        });
      });
  }, [token]);

  if (outcome.state === 'checking') {
    return (
      <AuthLayout title="Confirming your email">
        <div className="grid place-items-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (outcome.state === 'failed') {
    return (
      <AuthLayout title="This link cannot be used">
        <p className="text-sm text-slate-600">{outcome.message}</p>
        <p className="mt-4 text-sm text-slate-500">
          You can ask for a new confirmation email from Settings once you are signed in.
        </p>
        <Button className="mt-6 w-full" onClick={() => navigate('/login')}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Email confirmed" subtitle="Thank you — that is all we needed">
      {outcome.email && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          <strong className="text-slate-900">{outcome.email}</strong> is confirmed. Password
          resets and security notices will reach you here.
        </p>
      )}
      <Button className="mt-6 w-full" onClick={() => navigate('/')}>
        Continue
      </Button>
    </AuthLayout>
  );
}
